import React, { useEffect, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { createPortal } from "react-dom";

type HintPosition = "left" | "right" | "top" | "bottom";

const DEFAULT_DURATION_MS = 5000;

interface DisappearingHintProps {
    isVisible: boolean;
    message: string;
    position: HintPosition;
    useRelativePositioning?: boolean;
    fixedPosition?: { top?: number; right?: number; bottom?: number; left?: number };
    duration?: number;
    onDismiss?: () => void;
}

const getPositionClasses = (position: HintPosition, useRelativePositioning: boolean) => {
    if (useRelativePositioning) {
        switch (position) {
            case "right":
                return "absolute left-full top-1/2 ml-3";
            case "left":
                return "absolute right-full top-1/2 mr-3";
            case "top":
                return "absolute bottom-full left-1/2 mb-3";
            case "bottom":
                return "absolute top-full left-1/2 mt-3";
        }
    }
    return "fixed";
};

const getAnimationValues = (position: HintPosition) => {
    switch (position) {
        case "right":
            return {
                initial: { x: -8, y: "-50%" },
                animate: { x: 0, y: "-50%" },
                exit: { x: -8, y: "-50%" }
            };
        case "left":
            return {
                initial: { x: 8, y: "-50%" },
                animate: { x: 0, y: "-50%" },
                exit: { x: 8, y: "-50%" }
            };
        case "top":
            return {
                initial: { y: 8, x: "-50%" },
                animate: { y: 0, x: "-50%" },
                exit: { y: 8, x: "-50%" }
            };
        case "bottom":
            return {
                initial: { y: -8, x: "-50%" },
                animate: { y: 0, x: "-50%" },
                exit: { y: -8, x: "-50%" }
            };
    }
};

const getArrowClasses = (position: HintPosition) => {
    switch (position) {
        case "right":
            return "absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-gray-900";
        case "left":
            return "absolute left-full top-1/2 -translate-y-1/2 border-4 border-transparent border-l-gray-900";
        case "top":
            return "absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900";
        case "bottom":
            return "absolute bottom-full left-1/2 -translate-x-1/2 border-4 border-transparent border-b-gray-900";
    }
};

export function DisappearingHint({
    isVisible,
    message,
    position,
    useRelativePositioning = false,
    fixedPosition,
    duration = DEFAULT_DURATION_MS,
    onDismiss
}: DisappearingHintProps) {
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const positionClasses = getPositionClasses(position, useRelativePositioning);
    const animation = getAnimationValues(position);
    const arrowClasses = getArrowClasses(position);

    // Auto-dismiss hint
    useEffect(() => {
        if (isVisible) {
            timeoutRef.current = setTimeout(() => {
                onDismiss?.();
            }, duration);
        }
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, [isVisible, duration, onDismiss]);

    const hint = (
        <AnimatePresence>
            {isVisible && (
                <motion.div
                    className={`pointer-events-none z-50 whitespace-nowrap rounded-lg bg-gray-900 px-3 py-2 text-xs font-medium text-white shadow-md ${positionClasses}`}
                    style={fixedPosition || {}}
                    initial={{ opacity: 0, ...animation.initial }}
                    animate={{ opacity: 1, ...animation.animate }}
                    exit={{ opacity: 0, ...animation.exit }}
                    transition={{ duration: 0.2, ease: "easeOut" }}
                >
                    {message}
                    <div className={arrowClasses} />
                </motion.div>
            )}
        </AnimatePresence>
    );

    // Use portal for fixed positioning, render directly for relative positioning
    return useRelativePositioning ? hint : createPortal(hint, document.body);
}
